import { AntSelectPropsGeneric, AntSelectValue } from 'controls/react/ant/ant-select/types';
import { BaseOptionType } from '@rc-component/select/lib/Select';
import React from 'react';

export function useCloseOnMouseLeave<VT extends AntSelectValue, OptionType extends BaseOptionType>(
	props: AntSelectPropsGeneric<VT, OptionType>,
) {
	// by this point in the pipeline onDropdownVisibleChange has already been normalized to
	// onOpenChange by useOnVisibilityChangeToTriggerOnChange, which runs earlier
	const { onMouseEnter, onMouseLeave, closeOnMouseLeave, onOpenChange, popupRender, ...rest } = props;

	const mouseLeftTimeoutRef = React.useRef<number>(null);
	const [open, setOpen] = React.useState(false);
	const closeOnLeave = closeOnMouseLeave === true || props.mode == 'tags' || props.mode == 'multiple';

	const cancelClose = React.useCallback(() => {
		if (mouseLeftTimeoutRef.current) {
			clearTimeout(mouseLeftTimeoutRef.current);
			mouseLeftTimeoutRef.current = null;
		}
	}, []);

	const scheduleClose = React.useCallback(() => {
		if (mouseLeftTimeoutRef.current == null) {
			mouseLeftTimeoutRef.current = setTimeout(() => {
				setOpen(false);
				mouseLeftTimeoutRef.current = null;
			}, 300);
		}
	}, []);

	const onMouseEnterWrapper = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (closeOnLeave) {
				cancelClose();
			}

			onMouseEnter?.(e);
		},
		[onMouseEnter, cancelClose],
	);

	const onMouseLeaveWrapper = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (closeOnLeave) {
				scheduleClose();
			}
			onMouseLeave?.(e);
		},
		[onMouseLeave, scheduleClose],
	);

	const onOpenChangeWrapper = React.useCallback(
		(open: boolean) => {
			setOpen(open);
			onOpenChange?.(open);
		},
		[closeOnLeave, onOpenChange],
	);

	// the dropdown is portalled to document.body, so it's a separate DOM subtree from the select's
	// own trigger element - a mouseenter/mouseleave attached to the trigger never sees events that
	// originate inside the dropdown. Track hover on the popup itself via popupRender instead, so
	// moving the mouse from the trigger into the dropdown to pick an option doesn't get treated as
	// "left the control" and close it mid-selection.
	const popupRenderWrapper = React.useCallback(
		(menu: React.ReactElement) => {
			const inner = popupRender ? popupRender(menu) : menu;

			if (!closeOnLeave) {
				return inner;
			}

			return (
				<div onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
					{inner}
				</div>
			);
		},
		[popupRender, closeOnLeave, cancelClose, scheduleClose],
	);

	return {
		onMouseEnter: onMouseEnterWrapper,
		onMouseLeave: onMouseLeaveWrapper,
		onOpenChange: onOpenChangeWrapper,
		popupRender: popupRenderWrapper,
		open: closeOnLeave ? open : undefined,
		...rest,
	};
}
